import {
  action_crossed_swords,
  face_smile,
  etc_grape,
  body_walking_man,
  adults_only_kissing_couple,
  radio_selected,
  radio_unselected
} from '@assets/icons';
import { CheckboxItem, Frame, MenuTop, Select, TagSearchInput, Text } from '@components';
import { SelectedComicOptionEnum, StandardGenreValueEnum } from '@constant';
import { useMain } from '@hooks';
import { Button, MenuItem, Radio } from '@mui/material';
import { colorSet } from '@theme';

const MainPage = () => {
  const {
    selectedOption,
    handleOptionChange,
    selectedGenreList,
    handleGenreToggle,
    isGenreSelected,
    selectedComicNameList,
    inputKeyword,
    autocompleteList,
    handleKeyDown,
    handleInputKeywordChange,
    handleAutocompleteChange,
    handleComicTagClear,
    isSearchButtonEnabled,
    handleSearchButtonClick,
    handleAdultsOnlyClick,
    handleActionGenreClick,
    handleBodyPartClick,
    handleFacialExpressionClick,
    handleEtcClick
  } = useMain();

  // StandardGenreValueEnum의 값들을 배열로 변환
  const genreOptions = Object.values(StandardGenreValueEnum);

  return (
    <>
      <MenuTop />

      {/* 스탠다드 검색 */}
      <Frame width={1200} margin="48px 0px 80px 0px">
        <div style={{ marginBottom: '32px' }}>
          <Text variant="h3" fontWeight="bold" marginBottom="8px">
            스탠다드 검색
          </Text>
          <Text variant="body1">검색을 위해 ‘장르’ 또는 ‘작품명’ 중 하나를 선택해 주세요.</Text>
        </div>

        <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-end' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
            {/* 스탠다드 검색 옵션: 장르 */}
            <div>
              <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', marginBottom: '8px' }}>
                <Radio
                  checked={selectedOption === SelectedComicOptionEnum.GENRE}
                  onChange={() => handleOptionChange(SelectedComicOptionEnum.GENRE)}
                  checkedIcon={<img src={radio_selected} alt="" style={{ width: 20, height: 20 }} />}
                  icon={<img src={radio_unselected} alt="" style={{ width: 20, height: 20 }} />}
                />
                <Text variant="subtitle" fontWeight="bold">
                  장르
                </Text>
              </div>

              <Select
                disabled={selectedOption !== SelectedComicOptionEnum.GENRE}
                multiple={true}
                value={selectedGenreList}
                renderValue={(selected: any) => {
                  if (selected && selected.length > 0) {
                    return selected.join(', ');
                  } else {
                    return (
                      <Text variant="body1" color={colorSet.gray400}>
                        {selectedOption !== SelectedComicOptionEnum.GENRE
                          ? '작품명 입력 시 장르는 자동 선택됩니다.'
                          : '장르를 1개 이상 선택해주세요.'}
                      </Text>
                    );
                  }
                }}
                boxstyle={{ width: 344 }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      maxHeight: 386,
                      '&::-webkit-scrollbar': {
                        width: '8px',
                        height: '0px'
                      },
                      '&::-webkit-scrollbar-thumb': {
                        borderRadius: '8px',
                        backgroundColor: colorSet.primaryMain
                      },
                      '&::-webkit-scrollbar-track': {
                        borderRadius: '8px',
                        backgroundColor: colorSet.gray200
                      },
                      '& .MuiMenuItem-root.Mui-selected': {
                        backgroundColor: colorSet.white
                      }
                    }
                  }
                }}
              >
                <MenuItem value={''} disabled>
                  장르
                </MenuItem>
                {genreOptions.map((genre) => (
                  <MenuItem key={genre} value={genre} onClick={() => handleGenreToggle(genre)}>
                    <CheckboxItem
                      label={genre}
                      isChecked={isGenreSelected(genre)}
                      onChange={() => handleGenreToggle(genre)}
                    />
                  </MenuItem>
                ))}
              </Select>
            </div>

            {/* 스탠다드 검색 옵션: 작품명 */}
            <div>
              <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', marginBottom: '8px' }}>
                <Radio
                  checked={selectedOption === SelectedComicOptionEnum.COMIC_NAME}
                  onChange={() => handleOptionChange(SelectedComicOptionEnum.COMIC_NAME)}
                  checkedIcon={<img src={radio_selected} alt="" style={{ width: 20, height: 20 }} />}
                  icon={<img src={radio_unselected} alt="" style={{ width: 20, height: 20 }} />}
                />
                <Text variant="subtitle" fontWeight="bold">
                  작품명
                </Text>
              </div>

              <TagSearchInput
                placeholder={
                  selectedOption !== SelectedComicOptionEnum.COMIC_NAME
                    ? '장르 선택 시 해당 장르의 작품이 전체 검색됩니다.'
                    : '작품명 입력 후 엔터를 누르세요.'
                }
                tags={selectedComicNameList}
                inputValue={inputKeyword}
                options={autocompleteList}
                onKeyDown={handleKeyDown}
                onInputChange={handleInputKeywordChange}
                onAutocompleteChange={handleAutocompleteChange}
                onTagClear={handleComicTagClear}
                disabled={selectedOption !== SelectedComicOptionEnum.COMIC_NAME}
                boxstyle={{ width: 344 }}
              />
            </div>
          </div>
          {/* 스탠다드 검색 버튼 */}
          <div
            style={{
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-end',
              position: 'relative'
            }}
          >
            <Button
              disabled={!isSearchButtonEnabled()}
              variant="contained"
              size="large"
              onClick={handleSearchButtonClick}
            >
              검색
            </Button>
            {!isSearchButtonEnabled() ? (
              <Text variant="body1" color="warning" style={{ position: 'absolute', top: 52 }}>
                장르 또는 작품명을 1개 이상 선택해 주세요.
              </Text>
            ) : null}
          </div>
        </div>
      </Frame>

      {/* 스페셜 단추 */}
      <Frame width={1200} margin="0px 0px 96px 0px">
        <Text variant="h3" fontWeight="bold" marginBottom="40px">
          스페셜 단추
        </Text>

        <div style={{ display: 'flex', flexDirection: 'row', gap: 150 }}>
          <div
            style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: 120, cursor: 'pointer' }}
            onClick={handleAdultsOnlyClick}
          >
            <img src={adults_only_kissing_couple} alt="" style={{ width: 120, height: 120, marginBottom: '6px' }} />
            <Text variant="subtitle" fontWeight="bold">
              19금
            </Text>
          </div>
          <div
            style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: 120, cursor: 'pointer' }}
            onClick={handleActionGenreClick}
          >
            <img src={action_crossed_swords} alt="" style={{ width: 120, height: 120, marginBottom: '6px' }} />
            <Text variant="subtitle" fontWeight="bold">
              액션 장르
            </Text>
          </div>
          <div
            style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: 120, cursor: 'pointer' }}
            onClick={handleBodyPartClick}
          >
            <img src={body_walking_man} alt="" style={{ width: 120, height: 120, marginBottom: '6px' }} />
            <Text variant="subtitle" fontWeight="bold">
              신체부위
            </Text>
          </div>
          <div
            style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: 120, cursor: 'pointer' }}
            onClick={handleFacialExpressionClick}
          >
            <img src={face_smile} alt="" style={{ width: 120, height: 120, marginBottom: '6px' }} />
            <Text variant="subtitle" fontWeight="bold">
              표정
            </Text>
          </div>
          <div
            style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: 120, cursor: 'pointer' }}
            onClick={handleEtcClick}
          >
            <img src={etc_grape} alt="" style={{ width: 120, height: 120, marginBottom: '6px' }} />
            <Text variant="subtitle" fontWeight="bold">
              기타
            </Text>
          </div>
        </div>
      </Frame>
    </>
  );
};

export default MainPage;
